/**
 * Authentication API service
 * Handles all auth-related API calls
 */

import api from './api'
import {
  TokenResponse,
  AuthUser,
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  VerifyEmailRequest,
  AUTH_STORAGE_KEYS,
} from '@/types/auth'

// Re-export AUTH_STORAGE_KEYS for convenience
export { AUTH_STORAGE_KEYS }

/**
 * Auth API endpoints
 */
export const authApi = {
  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/auth/login', data)
    return response.data
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/auth/register', data)
    return response.data
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    })
    return response.data
  },

  /**
   * Logout (invalidate tokens)
   */
  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refresh_token: refreshToken })
      } catch (error) {
        // Ignore errors during logout - token might already be invalid
      }
    }
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<AuthUser> {
    const response = await api.get<AuthUser>('/auth/me')
    return response.data
  },

  /**
   * Get current user permissions from the database.
   * Follows Consumer Duty pattern: permissions are fetched separately from /auth/permissions,
   * not from the /auth/me response or the Auth0 token.
   */
  async getPermissions(): Promise<{ permissions: string[]; roles: string[]; data_scopes: Record<string, string[]> }> {
    const response = await api.get<{ permissions: string[]; roles: string[]; data_scopes: Record<string, string[]> }>('/auth/permissions')
    return response.data
  },

  /**
   * Request password reset email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await api.post('/auth/forgot-password', data)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await api.post('/auth/reset-password', data)
  },

  /**
   * Change password (authenticated)
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await api.post('/auth/change-password', data)
  },

  /**
   * Verify email with token
   */
  async verifyEmail(data: VerifyEmailRequest): Promise<void> {
    await api.post('/auth/verify-email', data)
  },

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<void> {
    await api.post('/auth/resend-verification')
  },
}

/**
 * Token management utilities
 */
export const tokenManager = {
  /**
   * Store tokens in localStorage
   */
  setTokens(tokens: TokenResponse): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token)
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token)
    // Calculate expiry time
    const expiresAt = Date.now() + tokens.expires_in * 1000
    localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString())
    // Set auth header
    api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`
  },

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
  },

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  },

  /**
   * Get token expiry time
   */
  getTokenExpiresAt(): number | null {
    const expiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT)
    return expiresAt ? parseInt(expiresAt, 10) : null
  },

  /**
   * Check if token is expired or will expire soon
   */
  isTokenExpired(bufferSeconds: number = 60): boolean {
    const expiresAt = this.getTokenExpiresAt()
    if (!expiresAt) return true
    return Date.now() > expiresAt - bufferSeconds * 1000
  },

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT)
    delete api.defaults.headers.common['Authorization']
  },

  /**
   * Initialize auth from stored tokens
   */
  initFromStorage(): {
    accessToken: string | null
    refreshToken: string | null
    expiresAt: number | null
  } {
    const accessToken = this.getAccessToken()
    const refreshToken = this.getRefreshToken()
    const expiresAt = this.getTokenExpiresAt()

    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }

    return { accessToken, refreshToken, expiresAt }
  },
}

export default authApi
